import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import type { CoachCard, CoachOpening, CoachThreadMessage, EmailChoices, EvidenceReference, ExperienceLevel, FocusView, LearnerProfile, LibraryState, PracticeCurrentResponse, PracticeView, ProfileEvidence, ProfileQuestion, RehearsalReflection } from "@pushback-and-chaos/contracts";
import { EMAIL_CHOICES, FOCUS, HOME, PRACTICE, SKILL_LABEL } from "../design/copy.js";
import { EmailChoicesCard, type EmailChoicesFor } from "./email-choices.js";
import { focusCoverFor, practiceCoverFor } from "../design/covers.js";
import { CardRows, CaseKindRow, CasePaperCover, CoverSprites, PaperRows, SymbolCover, type CardGlyph } from "../ui/case-card.js";
import { DeskLightbox } from "../ui/desk-lightbox.js";
import { FocusBody, FocusComposer, focusSkillWord, type FocusBodyProps, type FocusComposerProps } from "./focus-panel.js";
import { PracticeBody, PracticeComposer, practiceCanWrite, stageWord, type PracticeBodyProps, type PracticeComposerProps } from "./practice-panel.js";
import { CoachPortrait } from "../ui/coach-portrait.js";
import { FreeTextInput } from "../ui/input.js";
import { markTyped, useTypewriter } from "../ui/typewriter.js";
import { LinkMark } from "./desk-link-mark.js";
import { DeskLinkOverlay, endSelector, type LinkLine } from "./desk-link-overlay.js";
import { linkedFiles, type LinkedFile } from "./desk-links.js";
import { CaseLightbox, StartOverConfirm, useCaseLightbox } from "../ui/case-lightbox.js";
import type { TranscribeFn } from "../ui/dictation.js";
import { clampDragDelta, usePointerDrag } from "../ui/use-drag.js";
import { Pager, PanelHeading } from "./storyteller/chrome.js";
import { ScrollPage, newestOf, splitPages, useReadingPlace, type Page } from "./storyteller/pages.js";
import { coachAtoms, coachUtterance, INTRO_KEY, recommendationKey } from "./coach-atoms.js";
import { stackByKind, tidy, type Point, type StackablePaper } from "./desk-arrange.js";
import { ArrangeMenu, type ArrangeChoice } from "./desk-arrange-menu.js";
import { ToolGlyph } from "./desk-tool-glyph.js";
import type { LibraryCase } from "./library.js";
import { trackLabel } from "./library-shelves.js";

/**
 * THE FRONT DOOR (home pass, 2026-09-09; laid out like the desk, 2026-09-12;
 * rebuilt in the Storyteller's vocabulary, 2026-09-20) — the coach is the
 * first thing a learner meets, and the page it meets them on is the play
 * screen's own composition with one region fewer:
 *
 *   +-------------------+-------------------------+
 *   |                   |                         |
 *   |    THE COACH      |        THE DESK         |
 *   |  [reply composer] |                         |
 *   +-------------------+-------------------------+
 *            43%                     57%
 *
 * The conversation leads on the left in the play surface's conversation
 * panel — its paper, its hairline, its banner ("CHAT WITH YOUR COACH",
 * 2026-09-22), its composer — and the desk the
 * coach deals onto is the right-hand column in the desk panel's own paper.
 * The whole thing fills the window; nothing scrolls but the conversation.
 *
 * TWO DEPARTURES from that panel, both deliberate. The coach's own prose
 * stays in the sans face (`.pcs-coach`) rather than taking the fiction's
 * storybook serif — the coach is not in the fiction. And the conversation
 * scrolls where the story pages, because a page control says "3 / 7" and
 * this surface renders no numeral.
 *
 * THE CARDS ARE PAPER (2026-09-22): the play desk's document card grown to
 * a case file — square, straight, the kind row leading with the door word,
 * the painting framed, the reason under the rows (ui/case-card.tsx).
 *
 * WHAT HAPPENS ON ARRIVAL. The coach speaks first: three paragraphs the
 * server assembled — what the record shows going well, what is improving,
 * what to work on next and in which case (GET /coach/opening). Then the
 * cards the coach pushes land on the desk, one after another, with the
 * desk's own arrival grammar. A press on a card brings it to the top of the
 * stack, never into the case (a learner needs to look before being thrown
 * in); a press that moves drags it anywhere on the page — across the rule
 * onto the conversation if they like — and it stays where it is put. Two
 * doors at the foot of a card: "open." / "walk back in." enters the case,
 * and "details." opens the library's own lightbox — one at a time, minimised
 * by a press anywhere else.
 *
 * THE ONE LINE OF TUTORIAL. "click and drag." fades in over the desk a few
 * seconds after the cards land if the learner has not touched one, fades
 * out on its own, and comes back after a couple of minutes without a touch.
 *
 * ARRANGING THE DESK (2026-10-31, Andre; staged behind `arrange`, off in
 * production until reviewed). "tidy." at the desk's foot is a command, not a
 * mode: it measures the papers, works out once where they would lie
 * (desk-arrange.ts), moves them there through the same remembered placement
 * a drag writes, and offers one "undo." until the learner moves something or
 * the desk changes. Its first form laid readable rows; Andre's second pass
 * the same day asked for PILES BY KIND instead — the coach's cases in one
 * pile, the plan, the focus, the practice papers and the email card each in
 * their own, side by side when the desk is wide enough and in the columns it
 * does hold when it is not, the paper to open first lying whole at the head
 * of each pile and the rest filed behind it a strip apart — so a desk of
 * more than six reads as what to open next, by kind. Both forms are kept
 * while the choice is reviewed (`arrange`); the piles are the default.
 * Nothing is binned, no focus, exercise or case state is touched, and every
 * paper is as free to drag afterwards as before.
 *
 * WHAT THIS SURFACE REFUSES TO RENDER, by design and by test
 * (home.test.tsx): a numeral or a rank. The welcome is validated digit-free
 * on the server; the cards carry a kind, a title, three rows in words and a
 * reason; the composer is the play surface's own.
 */
export interface HomeScreenProps {
  /** Null while the opening is still arriving. */
  readonly opening: CoachOpening | null;
  /** True when the opening could not be fetched at all — the page says so in words and the composer still works. */
  readonly openingFailed: boolean;
  /** No finished engagement yet: the first opening is being written, and the waiting line says so honestly. */
  readonly firstVisit: boolean;
  /** No identity at all: the coach has nobody to read for, and the composer says so. */
  readonly gated: boolean;
  /**
   * An identity without a confirmed profile (pedagogy review, step 5): the
   * front door IS onboarding. The coach introduces itself and asks; the
   * learner answers in the composer; whatever is still missing is asked for,
   * one thing at a time (onboarding pass, 2026-09-13); the draft is read back
   * in the learner's own words and confirmed, corrected, or started over in
   * the thread. Then one rehearsal move before the first case. Null once the
   * profile is confirmed and the rehearsal is made or declined.
   */
  readonly onboarding: OnboardingState | null;
  readonly onOnboard: (prose: string) => void;
  /** A level picked from the offered four: applied with no model call. */
  readonly onAnswerLevel: (level: ExperienceLevel) => void;
  readonly onConfirmProfile: () => void;
  readonly onRedoProfile: () => void;
  readonly onRehearse: (text: string) => void;
  readonly onSkipRehearsal: () => void;
  readonly onBeginAfterRehearsal: () => void;
  /** The conversation as it was kept, oldest first. */
  readonly thread: readonly CoachThreadMessage[];
  /** The learner's line while its reply is being written, or null. */
  readonly pendingText: string | null;
  readonly busy: boolean;
  /** The last failure the page owner saw, or null: the thread says so in words rather than dropping the line. */
  readonly error: string | null;
  /** The published cases, for a card's track, goal and skill rows by slug. */
  readonly cases: readonly LibraryCase[];
  readonly onSend: (text: string) => void;
  /** "View the moment" (C05): opens the debrief at a moment the coach pointed at. */
  readonly onViewMoment?: ((reference: EvidenceReference) => void) | undefined;
  /**
   * THE DESK'S OTHER PAPERS (2026-09-23, Andre): the learner's focus and
   * practice exercise, as cards on the desk beside the coach's, opening into
   * a lightbox of their own. Null while arriving; absent on a surface that
   * has no desk.
   */
  readonly focus?: { readonly current: FocusView | null } | null;
  readonly practice?: PracticeCurrentResponse | null;
  /**
   * True once everything the desk shows has answered: the coach's hand, the
   * focus and the exercise. Until then a card missing from the desk is still
   * arriving, and its remembered place is kept for it.
   */
  readonly deskSettled?: boolean;
  readonly deskBusy?: boolean;
  /** The last refusal from a desk action, in the server's words, shown on the paper the learner is holding. */
  readonly deskError?: string | null;
  readonly onFocusSuggest?: FocusBodyProps["onSuggest"];
  readonly onFocusChoose?: FocusBodyProps["onChoose"];
  readonly onFocusAction?: FocusBodyProps["onAction"];
  readonly onFocusSelfReport?: FocusComposerProps["onSelfReport"];
  readonly onPracticeOffer?: PracticeBodyProps["onOffer"];
  readonly onPracticeAction?: PracticeBodyProps["onAction"];
  readonly onPracticeAttempt?: PracticeComposerProps["onAttempt"];
  /**
   * THE EMAIL CHOICES CARD (2026-10-07, Andre): on the desk from the first
   * Harborlight report on, permanently. Null or absent before then (the
   * server's `eligible`), and there is no card. It can be collapsed, never
   * binned; collapsing records nothing.
   */
  readonly emailChoices?: EmailChoicesFor | null;
  /** Whether the learner's recruiting season is set, for the card's season-needed line. */
  readonly emailSeasonSet?: boolean;
  /** True while an email save from either surface is pending; the card's controls wait for it. */
  readonly emailBusy?: boolean;
  readonly onSaveEmailChoices?: (choices: EmailChoices) => Promise<void>;
  /** The guest card's "sign in.". */
  readonly onSignIn?: () => void;
  readonly onOpenSettings: () => void;
  readonly dictate: TranscribeFn | null;
  /** The learner's shelf state, for the lightbox's own affordances; null while it is arriving. */
  readonly libraryState: LibraryState | null;
  /** A CONTINUE card's "walk back in.", and the lightbox's. */
  readonly onContinue: (sessionId: string, caseSlug: string) => void;
  /** A case card's "open.": the case, exactly as the library opens it. `replaceOpen` only after the "are you sure". */
  readonly onStart: (caseSlug: string, replaceOpen: boolean) => void;
  readonly onReadPrologue: (caseSlug: string) => void;
  /** The saved box, replaced whole. */
  readonly onSetSaved: (slugs: readonly string[]) => void;
  /** The PLAN card: the practice record, where the plan lives. */
  readonly onReadPlan: () => void;
  /**
   * THE ARRANGE CONTROLS (2026-10-31): "tidy." and its "undo." at the desk's
   * foot, and which form the tidy takes — piles by kind ("stacks", Andre's
   * second pass) or readable rows ("rows", the first form, kept for the
   * comparison). Absent, the desk is exactly as it was — production stays
   * there until Andre has reviewed the control on the bench; the front-door
   * review bench turns it on.
   */
  readonly arrange?: ArrangeStyle | undefined;
}

/** The forms a tidy can take: piles by kind, or rows in reading order. */
export type ArrangeStyle = "stacks" | "rows";

/** Card geometry, in rem: the library's card width; the desk stacks them a step apart, alternating a small lean, like the desk's own documents. */
const CARD_W_REM = 17;
const STACK_STEP_REM = 3.25;
const STACK_INSET_REM = 1.5;
const STACK_LEAN_REM = 1.25;
/** The tightest a pile's strips may close when the desk is short, in rem: a paper's kind row and the padding above it, so each filed paper still says what it is. */
const PILE_MIN_STEP_REM = 2;
/**
 * THE PILES, left to right, when the desk is tidied by kind (2026-10-31,
 * Andre): the coach's cases, the development plan, the focus, the practice
 * papers (the exercise under way or offered first, the practice card itself
 * last), the email card. A provisional order, Andre's to change.
 */
const PILE_KINDS: readonly string[] = ["case", "plan", "focus", "practice", "email"];
/**
 * THE OTHER ARRANGEMENTS (2026-10-31, Andre, from the ideation's table of
 * controls), behind "arrange by.": each is the same piling as the tidy, with
 * the papers dealt to different piles.
 *
 * - "status.": each case by its standing with the coach — a pile for what is
 *   open, one for what AJ pushed, the calibration cases, the one to take
 *   next, what the learner saved, what came from the shelf — then the desk's
 *   own papers in their kind piles. The order is a provisional reading of the
 *   ideation's "continuing cases, cases from AJ, and other available cases".
 * - "AJ's order.": the coach's whole hand (the plan too) in ONE pile, as it
 *   was dealt — the first card's strip at the top, the last lying whole — so
 *   the arrangement restores an order that actually happened, with the
 *   desk's own papers in their piles beside it.
 * - "skill.": every paper that declares a skill — a case's primary
 *   dimension, the focus's, an exercise's — in a pile for that skill, in the
 *   order the house names the skills; the papers that declare none (the plan,
 *   the practice card, the email) in their own piles after.
 * - "my focus." (2026-10-31, the push's focus link): ONE pile for the focus,
 *   the focus paper lying whole with the cases AJ pushed FOR THIS FOCUS filed
 *   above it in the hand's order, and every other paper in its kind pile.
 *   Only a recorded link counts (the card's `focusId` is the current focus's
 *   id; coach_pushes.focus_id, migration 0050): a case whose skill happens to
 *   be the focus's is not gathered, because, as the ideation has it, Kesslow
 *   can be a judgment case that helps someone practise calibration, and only
 *   AJ's explanation establishes that. A push made under an earlier focus
 *   keeps that focus and is not gathered under the new one. The choice is
 *   offered only while a focus is chosen and at least one card on the desk
 *   is linked to it; with nothing to gather there is nothing to offer.
 * Also from the ideation but not built here, because the desk has no such
 * fact to arrange by yet: "last opened" (needs opening times).
 */
type ArrangeBy = "kind" | "status" | "hand" | "skill" | "focus";
const STATUS_PILES: readonly string[] = ["continue", "pushed", "calibration", "next", "saved", "shelf", "plan", "focus", "practice", "email"];
const HAND_PILES: readonly string[] = ["hand", "focus", "practice", "email"];
const SKILL_PILES: readonly string[] = [...Object.keys(SKILL_LABEL), "case", "plan", "focus", "practice", "email"];
const FOCUS_PILES: readonly string[] = ["focus", "case", "plan", "practice", "email"];
const PILES_BY: Readonly<Record<ArrangeBy, readonly string[]>> = { kind: PILE_KINDS, status: STATUS_PILES, hand: HAND_PILES, skill: SKILL_PILES, focus: FOCUS_PILES };
/** Within a skill pile the cases come first, then the focus, then the exercises: standings well apart so the kinds never interleave. */
const SKILL_STANDING_FOCUS = 100;
const SKILL_STANDING_EXERCISE = 200;
/** How long the composer's rule keeps the light of a landed reply (glow 4, 2026-11-02); the sweep itself is 700ms. */
const REPLY_LANDED_MS = 900;
const EMERGE_STAGGER_MS = 140;
/** The hint's clock: how long after the cards land, how long it stays, how long before it returns. */
const HINT_AFTER_MS = 4_000;
const HINT_STAY_MS = 5_000;
const HINT_RETURN_MS = 120_000;
const HINT_LEAVE_MS = 420;

export interface OnboardingState {
  readonly stage: "intake" | "rehearsal";
  /** The rehearsal's one move as it was said; the intake itself lives in the kept conversation (AJ intake, 2026-09-24). */
  readonly exchanges: readonly { readonly from: "coach" | "you"; readonly text: string }[];
  /** What the coach is asking for now, or null when the draft can be read back. */
  readonly question: ProfileQuestion | null;
  /** The complete, unconfirmed profile awaiting "that's right." */
  readonly draft: LearnerProfile | null;
  /** The learner's own words behind each piece of the draft. */
  readonly evidence: readonly ProfileEvidence[];
  /** The rehearsal's reflection once the move has been made. */
  readonly rehearsal: RehearsalReflection | null;
}

/** A paper's resting place in the stack, by slot, in rem: the arithmetic `DeskPaper` draws and the arrangement reads back. */
const restingLeftRem = (slot: number): number => STACK_INSET_REM + (slot % 2) * STACK_LEAN_REM;
const restingTopRem = (slot: number): number => slot * STACK_STEP_REM;
/** A paper's height when it cannot be measured (a browser without layout), in rem: about a case paper's. */
const PAPER_H_FALLBACK_REM = 23;
/** The root font size in px, which the rem geometry above is drawn in; a browser that will not say gets the usual. */
function remInPx(): number {
  try {
    const size = Number.parseFloat(globalThis.getComputedStyle(globalThis.document.documentElement).fontSize);
    return Number.isFinite(size) && size > 0 ? size : 16;
  } catch {
    return 16;
  }
}

/**
 * WHERE THE PAPERS WERE LEFT (2026-09-23, Andre): each card's slot in the
 * stack and the offset the learner dragged it to, remembered in the browser so
 * a return to Home finds the desk as it was left. A slot, once given, is a
 * card's own until the card leaves the desk: a new paper takes the lowest free
 * slot, so nothing already on the desk moves when one arrives or goes. A
 * browser convenience, never a record; a browser without storage starts tidy.
 */
const LAYOUT_KEY = "pc.desk.layout";
interface DeskLayout {
  readonly offsets: Readonly<Record<string, Point>>;
  readonly slots: Readonly<Record<string, number>>;
}
function readLayout(): DeskLayout {
  try {
    const raw = globalThis.localStorage.getItem(LAYOUT_KEY);
    if (raw === null) return { offsets: {}, slots: {} };
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return { offsets: {}, slots: {} };
    const offsetsRaw = (parsed as Record<string, unknown>)["offsets"];
    const slotsRaw = (parsed as Record<string, unknown>)["slots"];
    const offsets: Record<string, Point> = {};
    if (typeof offsetsRaw === "object" && offsetsRaw !== null) {
      for (const [key, value] of Object.entries(offsetsRaw as Record<string, unknown>)) {
        if (typeof value === "object" && value !== null && typeof (value as Point).x === "number" && typeof (value as Point).y === "number") offsets[key] = { x: (value as Point).x, y: (value as Point).y };
      }
    }
    const slots: Record<string, number> = {};
    if (typeof slotsRaw === "object" && slotsRaw !== null) {
      for (const [key, value] of Object.entries(slotsRaw as Record<string, unknown>)) if (typeof value === "number" && Number.isInteger(value) && value >= 0) slots[key] = value;
    }
    return { offsets, slots };
  } catch {
    return { offsets: {}, slots: {} };
  }
}
function writeLayout(layout: DeskLayout): void {
  try {
    globalThis.localStorage.setItem(LAYOUT_KEY, JSON.stringify(layout));
  } catch {
    /* a browser without storage forgets on reload, which is only the desk tidied */
  }
}
/**
 * Every present key keeps its slot; a newcomer takes the lowest slot no other
 * key holds. The absent are forgotten only once the whole desk has arrived
 * (`forgetAbsent`): while it is still arriving an absent card is one not yet
 * here, not one gone, so it keeps its slot and nobody takes it (2026-09-24:
 * a reload wiped the remembered desk, because the first render had no cards).
 */
export function assignSlots(previous: Readonly<Record<string, number>>, keys: readonly string[], forgetAbsent: boolean): Readonly<Record<string, number>> {
  const next: Record<string, number> = {};
  const taken = new Set<number>();
  for (const key of keys) { const slot = previous[key]; if (slot !== undefined && !taken.has(slot)) { next[key] = slot; taken.add(slot); } }
  if (!forgetAbsent) {
    for (const [key, slot] of Object.entries(previous)) if (!keys.includes(key) && !taken.has(slot)) { next[key] = slot; taken.add(slot); }
  }
  for (const key of keys) {
    if (next[key] !== undefined) continue;
    let slot = 0;
    while (taken.has(slot)) slot += 1;
    next[key] = slot;
    taken.add(slot);
  }
  return next;
}

/** The coach's cards put in the bin, remembered per hand so they stay away until the coach deals again; a browser convenience, never a record. */
const DISMISSED_KEY = "pc.desk.dismissed";
function readDismissed(openingKey: string | null): readonly string[] {
  if (openingKey === null) return [];
  try {
    const raw = globalThis.localStorage.getItem(DISMISSED_KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return [];
    const keys = (parsed as Record<string, unknown>)[openingKey];
    return Array.isArray(keys) ? keys.filter((k): k is string => typeof k === "string") : [];
  } catch {
    return [];
  }
}
function writeDismissed(openingKey: string | null, keys: ReadonlySet<string>): void {
  if (openingKey === null) return;
  try {
    globalThis.localStorage.setItem(DISMISSED_KEY, JSON.stringify({ [openingKey]: [...keys] }));
  } catch {
    /* a browser without storage forgets on reload, which is only the coach's hand coming back */
  }
}

export function HomeScreen(props: HomeScreenProps): ReactNode {
  const { opening, thread, pendingText, busy, gated, openingFailed, onboarding, firstVisit } = props;
  const regionRef = useRef<HTMLDivElement | null>(null);
  /** Where each card has been put, as an offset from where it emerged; remembered in the browser. */
  const [offsets, setOffsets] = useState<Readonly<Record<string, Point>>>(() => readLayout().offsets);
  /** Each card's slot in the stack, its own until it leaves the desk; remembered in the browser. */
  const [slots, setSlots] = useState<Readonly<Record<string, number>>>(() => readLayout().slots);
  /** Cards still playing their emerge animation. */
  const [emerging, setEmerging] = useState<ReadonlySet<string>>(() => new Set());
  const [touchedAt, setTouchedAt] = useState<number | null>(null);
  /** Cards the learner has pressed, oldest first: the last pressed is on top of the hand. */
  const [raised, setRaised] = useState<readonly string[]>([]);
  const box = useCaseLightbox();
  const [hint, setHint] = useState<"hidden" | "in" | "out">("hidden");
  /** How many times the hint has come and gone: the first showing is soon, every later one waits. */
  const [hintCycle, setHintCycle] = useState(0);

  // AN EMPTY DESK TO BEGIN WITH (AJ intake, 2026-09-24, Andre): nothing is on
  // the desk until the coach has heard enough to put papers there. The hand
  // the server deals a newcomer is held back while the intake is under way.
  const intaking = onboarding?.stage === "intake";
  const cards = useMemo(() => (intaking ? [] : (opening?.cards ?? [])), [opening, intaking]);
  // A card's key names the card, not its place in the hand, so a hand dealt again in
  // another order (a reload, a return after a while) still finds each card where it was left.
  const cardKeys = useMemo(() => {
    const seen = new Set<string>();
    return cards.map((card, index) => {
      const key = `${card.kind}:${card.caseSlug ?? "plan"}:${card.sessionId ?? ""}`;
      const unique = seen.has(key) ? `${key}:${String(index)}` : key;
      seen.add(unique);
      return unique;
    });
  }, [cards]);
  const cardKey = (_card: CoachCard, index: number): string => cardKeys[index] ?? String(index);

  // A card this page has not shown before rises from behind the panel; from
  // then on it is simply there. The first hand arrives together; a card the
  // coach pushes mid-conversation arrives alone, into the same hand, without
  // replaying the others (decision 8).
  const seenKeys = useRef<Set<string>>(new Set());
  const openingKey = opening === null ? null : `${opening.generatedAt}:${String(opening.cards.length)}`;
  useEffect(() => {
    if (opening === null) return;
    const fresh = cards.map(cardKey).filter((key) => !seenKeys.current.has(key));
    if (fresh.length === 0) return;
    for (const key of fresh) seenKeys.current.add(key);
    setEmerging((prev) => new Set([...prev, ...fresh]));
    if (fresh.length === cards.length) setTouchedAt(null);
    // Keyed on the opening's identity: `cards` follows it.
  }, [openingKey]);

  const onEmerged = useCallback((key: string) => {
    setEmerging((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  // THE HINT'S CLOCK. Runs only while there are cards; a touch resets it and
  // hides the line at once. Timers, never a poll.
  const cardCount = cards.length;
  useEffect(() => {
    if (cardCount === 0) {
      setHint("hidden");
      return;
    }
    let leave: ReturnType<typeof setTimeout> | null = null;
    let gone: ReturnType<typeof setTimeout> | null = null;
    const show = setTimeout(() => {
      setHint("in");
      leave = setTimeout(() => {
        setHint("out");
        gone = setTimeout(() => {
          setHint("hidden");
          // Gone; the clock starts again, and this time it waits.
          setHintCycle((n) => n + 1);
        }, HINT_LEAVE_MS);
      }, HINT_STAY_MS);
    }, touchedAt === null && hintCycle === 0 ? HINT_AFTER_MS : HINT_RETURN_MS);
    return () => {
      clearTimeout(show);
      if (leave !== null) clearTimeout(leave);
      if (gone !== null) clearTimeout(gone);
    };
  }, [cardCount, touchedAt, openingKey, hintCycle]);

  const closeBox = box.close;
  const touchCard = useCallback(
    (key: string) => {
      setHint("hidden");
      setTouchedAt(Date.now());
      setRaised((prev) => [...prev.filter((k) => k !== key), key]);
      closeBox();
    },
    [closeBox],
  );

  /** The card's own door into the case: the same thing the library's lightbox does. */
  const enter = useCallback(
    (card: CoachCard) => {
      if (card.kind === "PLAN") {
        props.onReadPlan();
        return;
      }
      if (card.caseSlug === null) return;
      if (card.kind === "CONTINUE" && card.sessionId !== null) {
        props.onContinue(card.sessionId, card.caseSlug);
        return;
      }
      const open = props.libraryState?.openEngagements.find((e) => e.caseSlug === card.caseSlug) ?? null;
      if (open !== null) props.onContinue(open.sessionId, open.caseSlug);
      else props.onStart(card.caseSlug, false);
    },
    [props],
  );

  /*
   * A PRESS ANYWHERE IN THE PANEL FINISHES THE TYPING, and it is the same
   * press that puts a lightbox down. The typewriter's state stays DOWN in the
   * conversation and only its "finish" comes up, through a ref: a character
   * landing must not re-render this component, or the desk and its six cards
   * are reconciled thirty times a second and the page types at seven frames.
   */
  const finishTyping = useRef<() => void>(() => undefined);
  const onPanelPress = useCallback(() => {
    closeBox();
    finishTyping.current();
  }, [closeBox]);

  /*
   * THE COMPOSER OPENS WHEN THE COACH HAS ASKED (AJ intake, 2026-09-24,
   * Andre). While the introduction is still being typed to a new learner the
   * field is shut; the moment its last paragraph lands, the question, it opens,
   * takes focus, and its rule pulses once so the eye goes to it. Only the
   * introduction shuts the field: a later reply types while the learner may
   * already be writing back, and a press finishes it anyway.
   */
  const [coachTyping, setCoachTyping] = useState(false);
  const introducing = intaking && thread.length === 0;
  const introTyping = introducing && coachTyping;
  const [introHeard, setIntroHeard] = useState(false);
  useEffect(() => {
    if (introTyping) setIntroHeard(true);
  }, [introTyping]);
  const composerWake = introducing && introHeard && !coachTyping;
  /*
   * THE REPLY LANDS (2026-11-02, glow 4): the moment the coach's typing ends,
   * the composer's rule carries one light across (the study's stylesheet;
   * nothing in the book), and the class leaves a breath later. A ref holds
   * the last typing state, so only the fall from typing to quiet counts,
   * never the first render.
   */
  const [replyLanded, setReplyLanded] = useState(false);
  const wasTyping = useRef(false);
  useEffect(() => {
    const landed = wasTyping.current && !coachTyping;
    wasTyping.current = coachTyping;
    if (!landed) return undefined;
    setReplyLanded(true);
    const timer = setTimeout(() => { setReplyLanded(false); }, REPLY_LANDED_MS);
    return () => { clearTimeout(timer); };
  }, [coachTyping]);
  const composerField = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    if (composerWake) composerField.current?.focus();
  }, [composerWake]);

  const bySlug = useMemo(() => new Map(props.cases.map((c) => [c.slug, c] as const)), [props.cases]);

  // THE FOCUS AND THE EXERCISE AS PAPERS (2026-09-23): each is one more card
  // in the case card's design, after the coach's, with its own glyph and
  // painting; each opens into its own lightbox, where the session happens.
  const noop = (): void => undefined;
  const focus = props.focus ?? null;
  const practice = props.practice ?? null;
  const focusCurrent = focus?.current ?? null;
  /**
   * A card AJ pushed FOR THE CURRENT FOCUS: its recorded link names the focus
   * that is chosen now (CoachCard.focusId; migration 0050). Nothing else
   * counts: not the card's skill, not a push made under an earlier focus.
   */
  const linkedToFocus = useCallback(
    (card: CoachCard): boolean => card.kind === "PUSHED" && card.focusId !== undefined && focusCurrent !== null && focusCurrent.state.status === "CHOSEN" && card.focusId === focusCurrent.focusId,
    [focusCurrent],
  );
  const practiceCurrent = practice?.current ?? null;
  const focusFace: PaperFace | null = focus === null ? null : focusCurrent === null
    ? { kindWords: FOCUS.card.kind, glyph: "focus", coverId: focusCoverFor(null), title: FOCUS.card.noneTitle, rows: [[FOCUS.card.rowStanding, FOCUS.card.standingNone]], byline: FOCUS.card.noneByline, action: firstVisit ? FOCUS.card.doorName : FOCUS.card.doorNone, dataCard: "FOCUS" }
    : { kindWords: `${FOCUS.card.kind} · ${focusSkillWord(focusCurrent)}`, glyph: "focus", coverId: focusCoverFor(focusCurrent.state.dimension), title: focusCurrent.state.text, rows: [[FOCUS.card.rowSkill, focusSkillWord(focusCurrent)], [FOCUS.card.rowStanding, FOCUS.statuses[focusCurrent.state.status]]], byline: FOCUS.provenance[focusCurrent.state.origin], action: focusCurrent.state.status === "SUGGESTED" ? FOCUS.card.doorSuggested : FOCUS.card.doorChosen, dataCard: "FOCUS" };
  /** THE OFFERS ON THE DESK (2026-09-23, Andre): the generic paper stays, and every offer and the exercise in progress is a paper of its own, so a learner can pull up a few before choosing. */
  const exercises: readonly PracticeView[] = practice === null ? [] : [...(practiceCurrent === null ? [] : [practiceCurrent]), ...practice.offers];
  const practiceSkillOf = (exercise: PracticeView): string => SKILL_LABEL[exercise.brief.dimension] ?? exercise.brief.dimension.toLowerCase();
  const practiceFace: PaperFace | null = practice === null ? null
    : { kindWords: PRACTICE.card.kind, glyph: "practice", coverId: practiceCoverFor(null), title: PRACTICE.card.noneTitle, rows: [[PRACTICE.card.rowStage, PRACTICE.card.stageNone]], byline: PRACTICE.card.noneByline, action: PRACTICE.card.doorNone, dataCard: "PRACTICE" };
  const exerciseFace = (exercise: PracticeView): PaperFace => ({
    kindWords: `${PRACTICE.card.kind} · ${practiceSkillOf(exercise)}`, glyph: "practice", coverId: practiceCoverFor(exercise.brief.id), title: exercise.brief.title,
    rows: [[PRACTICE.card.rowSkill, practiceSkillOf(exercise)], [PRACTICE.card.rowStage, stageWord(exercise.state.stage, exercise.state.finished)]],
    byline: exercise.brief.target_behaviour, action: exercise.state.stage === "OFFERED" ? PRACTICE.card.doorOffered : PRACTICE.card.doorOpen, dataCard: "PRACTICE", dataCase: exercise.exerciseId,
  });
  const exerciseKey = (exercise: PracticeView): string => `practice:${exercise.exerciseId}`;
  const paperIds = [focusFace?.coverId, practiceFace?.coverId, ...exercises.map((e) => practiceCoverFor(e.brief.id))].flatMap((id) => (id === undefined || id === null ? [] : [id]));
  /** The focus card's first door: suggest one, choose the suggestion, or pick the chosen one up. */
  const enterFocus = (rect: DOMRect): void => {
    // With no finished engagement there is no record to suggest from (the server says so with a
    // refusal): the door opens the paper, where the naming box is.
    if (focusCurrent === null && firstVisit) { touchCard("focus"); box.openKind("focus", rect); return; }
    if (focusCurrent === null) { (props.onFocusSuggest ?? noop)(); return; }
    if (focusCurrent.state.status === "SUGGESTED") { (props.onFocusChoose ?? noop)(focusCurrent.focusId); return; }
    touchCard("focus");
    box.openKind("focus", rect);
  };
  /** The practice card's first door: ask for one, accept the offer, or pick the session up. */
  const enterPractice = (): void => { (props.onPracticeOffer ?? noop)(); };
  const enterExercise = (exercise: PracticeView, rect: DOMRect): void => {
    if (exercise.state.stage === "OFFERED") { (props.onPracticeAction ?? noop)(exercise.exerciseId, "accept"); return; }
    touchCard(exerciseKey(exercise));
    box.openKind("practice", rect, exercise.exerciseId);
  };
  const deskBusy = props.deskBusy ?? false;
  const focusBody = focus === null ? null : <FocusBody focus={focus} busy={deskBusy} onSuggest={props.onFocusSuggest ?? noop} onChoose={props.onFocusChoose ?? noop} onAction={props.onFocusAction ?? noop} />;
  const focusFoot = focus === null ? null : <FocusComposer focus={focus} busy={deskBusy} onSuggest={props.onFocusSuggest ?? noop} onSelfReport={props.onFocusSelfReport ?? noop} onOpenSettings={props.onOpenSettings} />;
  const practiceBodyFor = (exercise: PracticeView | null): ReactNode => practice === null ? null : <PracticeBody exercise={exercise} available={practice.available} busy={deskBusy} onOffer={props.onPracticeOffer ?? noop} onAction={props.onPracticeAction ?? noop} />;
  const practiceFootFor = (exercise: PracticeView | null): ReactNode => practice === null || !practiceCanWrite(exercise) ? null : <PracticeComposer exercise={exercise} busy={deskBusy} onAttempt={props.onPracticeAttempt ?? noop} onOpenSettings={props.onOpenSettings} />;
  const deskError = props.deskError ?? null;
  // THE EMAIL CHOICES CARD (2026-10-07): Codex's title and intro on the paper, its "open email
  // choices." as the door, and no kind words beyond the envelope; the choices are inside.
  const emailChoices = props.emailChoices ?? null;
  const emailFace: PaperFace | null = emailChoices === null ? null
    : { kindWords: "", glyph: "email", coverId: null, title: EMAIL_CHOICES.title, rows: [], byline: emailChoices.signedIn ? EMAIL_CHOICES.intro : EMAIL_CHOICES.guestIntro, action: EMAIL_CHOICES.reopen, dataCard: "EMAIL" };
  const emailBody = emailChoices === null ? null : (
    <EmailChoicesCard state={emailChoices} busy={deskBusy || (props.emailBusy ?? false)}seasonSet={props.emailSeasonSet ?? false} onSave={props.onSaveEmailChoices ?? (() => Promise.resolve())} onSignIn={props.onSignIn ?? noop} />
  );

  /**
   * THE BIN (2026-09-23, Andre): bottom right of the desk. A card dragged onto it
   * is gone: an offered or accepted exercise is declined, one further along is
   * stopped, a focus is declined, and one of the coach's cards is put away until
   * the coach deals a new hand. The two generic papers (a focus to name, an
   * exercise to ask for) are the desk's doors and snap back.
   */
  const binRef = useRef<HTMLDivElement | null>(null);
  const [binHot, setBinHot] = useState(false);
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(() => new Set(readDismissed(openingKey)));
  useEffect(() => { setDismissed(new Set(readDismissed(openingKey))); }, [openingKey]);

  /*
   * THE CONNECTIONS (2026-10-31, Andre, from the arrange-files ideation,
   * chunks 10 to 13; desk-links.ts). Each pushed card that knows the reply
   * that made it is one relationship, shown one at a time: pressing the
   * marker at either end opens it; pressing it again, pressing another, or
   * Escape closes it. Opening from the conversation brings the file to the
   * front where it lies, scrolled into view if it is off the screen, and
   * never moves it; opening from the file turns the book to the paragraph
   * and scrolls to it. While one is open, a line is drawn between the two
   * markers (DeskLinkOverlay), and, when the push was made for the focus
   * that is chosen now, a second, shorter line from the file to the focus
   * paper. A file that leaves the desk closes its connection.
   */
  const chosenFocusId = focusCurrent !== null && focusCurrent.state.status === "CHOSEN" ? focusCurrent.focusId : null;
  const links = useMemo(() => linkedFiles(cards, (_card, index) => cardKeys[index] ?? String(index), chosenFocusId), [cards, cardKeys, chosenFocusId]);
  const [openLink, setOpenLink] = useState<string | null>(null);
  const [reveal, setReveal] = useState<{ readonly key: string; readonly tick: number } | null>(null);
  const openFile = openLink === null ? null : (links.get(openLink) ?? null);
  useEffect(() => {
    if (openLink !== null && (!links.has(openLink) || dismissed.has(links.get(openLink)?.key ?? ""))) setOpenLink(null);
  }, [openLink, links, dismissed]);
  useEffect(() => {
    if (openLink === null) return;
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") setOpenLink(null); };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); };
  }, [openLink]);
  const selectLinkFromThread = useCallback((messageId: string) => {
    const file = links.get(messageId);
    if (file === undefined) return;
    setOpenLink((current) => (current === messageId ? null : messageId));
    touchCard(file.key);
    const paper = document.querySelector(endSelector("data-key", file.key));
    if (paper !== null) {
      const r = paper.getBoundingClientRect();
      const onScreen = r.bottom > 0 && r.right > 0 && r.top < window.innerHeight && r.left < window.innerWidth;
      // "Show on desk" (the ideation's off-screen end): the press itself brings the file into view. jsdom has no scrollIntoView.
      const scroller = paper as { scrollIntoView?: (options: ScrollIntoViewOptions) => void };
      if (!onScreen) scroller.scrollIntoView?.({ block: "nearest", inline: "nearest", behavior: stillness() ? "auto" : "smooth" });
    }
  }, [links, touchCard]);
  const selectLinkFromCard = useCallback((messageId: string) => {
    const line = thread.find((m) => m.id === messageId);
    const file = links.get(messageId);
    if (file === undefined) return;
    setOpenLink((current) => (current === messageId ? null : messageId));
    touchCard(file.key);
    if (line !== undefined) setReveal((prev) => ({ key: recommendationKey(line, file.title), tick: (prev?.tick ?? 0) + 1 }));
  }, [links, thread, touchCard]);
  const paperLinkOf = (card: CoachCard): PaperLink | undefined => {
    if (card.kind !== "PUSHED" || card.messageId === undefined || !links.has(card.messageId)) return undefined;
    const id = card.messageId;
    return { end: `card:${id}`, selected: openLink === id, tone: linkedToFocus(card) ? "focus" : "plain", onSelect: () => { selectLinkFromCard(id); } };
  };
  const lines = useMemo((): readonly LinkLine[] => {
    if (openFile === null) return [];
    const explanation: LinkLine = { id: `explanation:${openFile.messageId}`, from: endSelector("data-link-end", `thread:${openFile.messageId}`), to: endSelector("data-link-end", `card:${openFile.messageId}`), tone: "plain" };
    const toFocus: LinkLine = { id: `focus:${openFile.messageId}`, from: endSelector("data-link-end", `card:${openFile.messageId}`), to: endSelector("data-key", "focus"), tone: "focus" };
    return openFile.forFocus ? [explanation, toFocus] : [explanation];
  }, [openFile]);
  // THE STACK'S SLOTS AND MEMORY (2026-09-23). Every card on the desk, coach's and
  // the desk's own, in arrival order; each keeps its slot while it is here.
  // The desk's own papers wait for the coach's hand before they take a slot, so on a first visit the
  // coach's cards come first in the stack whichever request answers first (seen in the dev stack:
  // the papers arrived before the opening and took the top slots).
  // Seen live (2026-09-24): an opening can be present with no cards yet, so the hand itself is the
  // gate: cards, or an opening that deals none (the newcomer's authored welcome).
  const papersReady = !intaking && opening !== null && (cards.length > 0 || opening.kind === "NEWCOMER");
  const paperKeys = !papersReady ? [] : [...(focusFace === null ? [] : ["focus"]), ...(practiceFace === null ? [] : ["practice"]), ...exercises.map(exerciseKey), ...(emailFace === null ? [] : ["email"])];
  const presentKeys = [...cards.flatMap((card, index) => (dismissed.has(cardKey(card, index)) ? [] : [cardKey(card, index)])), ...paperKeys];
  const presentKeysJoined = presentKeys.join("|");
  // THE TOP CARD (2026-11-02, glow 1): the last paper the hand raised, if it is still on the desk; it keeps a quiet edge in the study.
  const topKey = [...raised].reverse().find((key) => presentKeys.includes(key)) ?? null;
  // A card is gone, and its place forgotten, only when the whole desk has arrived: the hand and
  // both papers. Before that an absent card is one still on its way (2026-09-24, Andre: a reload
  // or a return after a while must never lose how the papers were left).
  const forgetAbsent = papersReady && (props.deskSettled ?? false);
  useEffect(() => {
    const keys = presentKeysJoined.length === 0 ? [] : presentKeysJoined.split("|");
    setSlots((prev) => {
      const next = assignSlots(prev, keys, forgetAbsent);
      const same = Object.keys(next).length === Object.keys(prev).length && Object.entries(next).every(([k, v]) => prev[k] === v);
      return same ? prev : next;
    });
    if (!forgetAbsent) return;
    // A card gone from the desk takes its offset with it, so a card of the same key arrives tidy.
    setOffsets((prev) => {
      const kept = Object.fromEntries(Object.entries(prev).filter(([k]) => keys.includes(k)));
      return Object.keys(kept).length === Object.keys(prev).length ? prev : kept;
    });
  }, [presentKeysJoined, forgetAbsent]);
  useEffect(() => { writeLayout({ offsets, slots }); }, [offsets, slots]);

  // The desk's own papers rise from behind the panel the first time this page shows them, like the coach's cards.
  const paperKeysJoined = paperKeys.join("|");
  useEffect(() => {
    const keys = paperKeysJoined.length === 0 ? [] : paperKeysJoined.split("|");
    const fresh = keys.filter((key) => !seenKeys.current.has(key));
    if (fresh.length === 0) return;
    for (const key of fresh) seenKeys.current.add(key);
    setEmerging((prev) => new Set([...prev, ...fresh]));
  }, [paperKeysJoined]);
  const slotOf = (key: string, fallback: number): number => slots[key] ?? fallback;
  /*
   * ARRANGING THE DESK (2026-10-31). The surface is measured, each paper is
   * measured where it stands, the arithmetic says where each would lie, and
   * the answer is committed through the same `offsets` a drag writes — so it
   * is remembered like a drag, and a drag afterwards simply writes over it.
   * The placement before the command is kept for ONE undo, which goes away
   * the moment the learner moves a paper (so it can never erase their own
   * work) or the desk itself changes (a card dealt, binned, or gone).
   */
  const deskRef = useRef<HTMLDivElement | null>(null);
  const [beforeArrange, setBeforeArrange] = useState<{ readonly offsets: Readonly<Record<string, Point>>; readonly raised: readonly string[] } | null>(null);
  useEffect(() => { setBeforeArrange(null); }, [presentKeysJoined]);
  const place = useCallback((key: string, next: Point): void => {
    setOffsets((prev) => ({ ...prev, [key]: next }));
    setBeforeArrange(null);
  }, []);
  const arrangeStyle = props.arrange;
  const arrangeable = arrangeStyle !== undefined && presentKeys.length >= 2;
  /**
   * Which pile a paper lies in under a given arrangement, and its standing
   * there (0 lies whole at the pile's head; the rest are filed above it in
   * order). Tidied by kind: the coach deals the hand in its own order of
   * urgency — what it pushed, what is open, the one to take next, then the
   * rest — so a case's standing is its place in the hand; the plan is a pile
   * of its own; among the practice papers the exercise under way or offered
   * comes before the practice card itself, in the order the desk deals them.
   * The other arrangements deal the same papers to other piles (see THE
   * OTHER ARRANGEMENTS, above).
   */
  const pileOf = (key: string, by: ArrangeBy): { readonly kind: string; readonly standing: number } => {
    const hand = cardKeys.indexOf(key);
    const card = hand >= 0 ? cards[hand] : undefined;
    if (card !== undefined) {
      if (by === "status") return { kind: card.kind.toLowerCase(), standing: hand };
      if (by === "hand") return { kind: "hand", standing: cards.length - 1 - hand };
      // "my focus.": a card linked to the current focus is filed above the focus paper (standing 0), first dealt nearest it.
      if (by === "focus" && linkedToFocus(card)) return { kind: "focus", standing: 1 + hand };
      if (card.kind === "PLAN") return { kind: "plan", standing: hand };
      if (by === "skill") return { kind: (card.caseSlug === null ? undefined : bySlug.get(card.caseSlug)?.primaryDimension) ?? "case", standing: hand };
      return { kind: "case", standing: hand };
    }
    if (key === "focus") return { kind: by === "skill" && focusCurrent !== null ? focusCurrent.state.dimension : "focus", standing: by === "skill" ? SKILL_STANDING_FOCUS : 0 };
    if (key === "email") return { kind: "email", standing: 0 };
    const exercise = exercises.findIndex((e) => exerciseKey(e) === key);
    const brief = exercise >= 0 ? exercises[exercise] : undefined;
    if (by === "skill" && brief !== undefined) return { kind: brief.brief.dimension, standing: SKILL_STANDING_EXERCISE + exercise };
    return { kind: "practice", standing: exercise >= 0 ? exercise : exercises.length };
  };
  /** The other arrangements, as the menu offers them: id, word, hint. "my focus." only while there is something to gather. */
  const arrangeChoices: readonly (ArrangeChoice & { readonly id: Exclude<ArrangeBy, "kind"> })[] = [
    { id: "status", word: HOME.arrangeStatus, hint: HOME.arrangeStatusHint },
    { id: "hand", word: HOME.arrangeHand, hint: HOME.arrangeHandHint },
    { id: "skill", word: HOME.arrangeSkill, hint: HOME.arrangeSkillHint },
    ...(cards.some(linkedToFocus) ? [{ id: "focus" as const, word: HOME.arrangeFocus, hint: HOME.arrangeFocusHint }] : []),
  ];
  const isArrangeBy = (id: string): id is ArrangeBy => id === "kind" || id === "status" || id === "hand" || id === "skill" || id === "focus";
  const arrangeDesk = (by: ArrangeBy): void => {
    const surface = deskRef.current;
    if (surface === null) return;
    const rem = remInPx();
    const elements = new Map<string, HTMLElement>();
    for (const el of surface.querySelectorAll<HTMLElement>("[data-key]")) { const key = el.dataset["key"]; if (key !== undefined) elements.set(key, el); }
    const papers: StackablePaper[] = presentKeys.flatMap((key, at) => {
      const el = elements.get(key);
      if (el === undefined) return [];
      const slot = slotOf(key, at);
      return [{
        key, slot, ...pileOf(key, by),
        rest: { x: restingLeftRem(slot) * rem, y: restingTopRem(slot) * rem },
        offset: offsets[key] ?? { x: 0, y: 0 },
        width: el.offsetWidth > 0 ? el.offsetWidth : CARD_W_REM * rem,
        height: el.offsetHeight > 0 ? el.offsetHeight : PAPER_H_FALLBACK_REM * rem,
      }];
    });
    const size = { width: surface.clientWidth, height: surface.clientHeight };
    const origin = { x: STACK_INSET_REM * rem, y: 0 };
    // The rows form is the tidy's alone; the other arrangements are piles whatever the bench asked.
    const arrangement = by === "kind" && arrangeStyle === "rows"
      ? tidy(size, papers, { gap: rem, minStep: STACK_STEP_REM * rem, origin })
      : stackByKind(size, papers, { gap: rem, step: STACK_STEP_REM * rem, minStep: PILE_MIN_STEP_REM * rem, origin, kinds: PILES_BY[by] });
    setBeforeArrange({ offsets, raised });
    setOffsets((prev) => ({ ...prev, ...arrangement.offsets }));
    // The stack's order of standing follows the arrangement, so a cascade shows each paper's own top.
    setRaised(arrangement.order);
    setHint("hidden");
    setTouchedAt(Date.now());
    closeBox();
  };
  const tidyDesk = (): void => { arrangeDesk("kind"); };
  const undoArrange = (): void => {
    const before = beforeArrange;
    if (before === null) return;
    // Every paper goes back to where it was — a paper that had never been moved
    // back to having no offset at all. Nothing can have been placed since (a
    // placement, or a change of papers, takes the undo away), so the snapshot is whole.
    setOffsets(before.offsets);
    setRaised(before.raised);
    setBeforeArrange(null);
    setTouchedAt(Date.now());
  };

  const putAway = (key: string): void => {
    setBinHot(false);
    if (key === "focus") {
      if (focusCurrent !== null) (props.onFocusAction ?? noop)(focusCurrent.focusId, "decline");
      return;
    }
    if (key.startsWith("practice:")) {
      const exercise = exercises.find((e) => exerciseKey(e) === key);
      if (exercise === null || exercise === undefined) return;
      if (exercise.can.decline) (props.onPracticeAction ?? noop)(exercise.exerciseId, "decline");
      else if (exercise.can.conclude) (props.onPracticeAction ?? noop)(exercise.exerciseId, "conclude");
      return;
    }
    if (key === "practice" || key === "email") return;
    setDismissed((prev) => { const next = new Set(prev); next.add(key); writeDismissed(openingKey, next); return next; });
    closeBox();
  };
  const paperLightbox = (state: { readonly kind: "case" | "focus" | "practice" | "email"; readonly slug: string; readonly pos: Point; readonly nonce: number }, leaving: boolean): ReactNode => {
    if (state.kind === "email" && emailFace !== null) {
      return <DeskLightbox key={`${leaving ? "leave" : "open"}-email-${String(state.nonce)}`} kindWords={emailFace.kindWords} glyph="email" ariaLabel={emailFace.title} pos={state.pos} leaving={leaving} closeLabel={EMAIL_CHOICES.collapse} onMove={leaving ? noop : box.move} onClose={leaving ? noop : box.close}>{emailBody}</DeskLightbox>;
    }
    if (state.kind === "focus" && focusFace !== null) {
      return <DeskLightbox key={`${leaving ? "leave" : "open"}-focus-${String(state.nonce)}`} kindWords={focusFace.kindWords} glyph="focus" ariaLabel={focusFace.title} pos={state.pos} leaving={leaving} error={deskError} onMove={leaving ? noop : box.move} onClose={leaving ? noop : box.close} foot={focusFoot}>{focusBody}</DeskLightbox>;
    }
    if (state.kind === "practice" && practiceFace !== null) {
      const exercise = exercises.find((e) => e.exerciseId === state.slug) ?? null;
      const face = exercise === null ? practiceFace : exerciseFace(exercise);
      return <DeskLightbox key={`${leaving ? "leave" : "open"}-practice-${state.slug}-${String(state.nonce)}`} kindWords={face.kindWords} glyph="practice" ariaLabel={face.title} pos={state.pos} leaving={leaving} tall={exercise !== null} error={deskError} onMove={leaving ? noop : box.move} onClose={leaving ? noop : box.close} foot={practiceFootFor(exercise)}>{practiceBodyFor(exercise)}</DeskLightbox>;
    }
    return null;
  };
  const boxEntryFor = (slug: string): LibraryCase | null => bySlug.get(slug) ?? null;
  const boxEntry = box.lightbox === null || box.lightbox.kind !== "case" ? null : boxEntryFor(box.lightbox.slug);
  const savedSlugs = props.libraryState?.saved ?? [];

  return (
    // ONE COMPOSITION, the play surface's own, filling the window: the
    // conversation on the left, the desk on the right, and nothing above
    // either of them. `.pc-storyteller` is what carries the panels' tokens,
    // so this page is drawn from the play screen's vocabulary rather than
    // from a copy of it.
    <div className="pc-storyteller pcs-front-door">
      <DeskLinkOverlay lines={lines} />
      <div ref={regionRef} data-region="home" className="pcs-grid pcs-front-door-grid">
        <CoverSprites slugs={cards.flatMap((c) => (c.caseSlug === null ? [] : [c.caseSlug]))} ids={paperIds} />

        {/* THE CONVERSATION, then the composer pinned at its foot: the play
            surface's conversation panel, under its own banner. A press
            anywhere in here minimises any lightbox. */}
        <section
          aria-label="Your coach"
          data-region="coach"
          className="pcs-panel pcs-narration"
          onPointerDownCapture={onPanelPress}
        >
          {/* THE BANNER (Andre, 2026-09-22): the desk's own heading, over the
              conversation — a small tracked label on a hairline. The menu
              that used to sit here lives in the site header now. */}
          <PanelHeading label={HOME.coachLabel} />

          <CoachThread
            opening={opening}
            openingFailed={openingFailed}
            firstVisit={firstVisit}
            gated={gated}
            onboarding={onboarding}
            thread={thread}
            pendingText={pendingText}
            busy={busy}
            error={props.error}
            finishRef={finishTyping}
            onAnswerLevel={props.onAnswerLevel}
            onConfirmProfile={props.onConfirmProfile}
            onRedoProfile={props.onRedoProfile}
            onSkipRehearsal={props.onSkipRehearsal}
            onBeginAfterRehearsal={props.onBeginAfterRehearsal}
            onViewMoment={props.onViewMoment}
            onTyping={setCoachTyping}
            links={links}
            selectedLink={openLink}
            onSelectLink={selectLinkFromThread}
            reveal={reveal}
          />

          <div className={["pcs-composer", composerWake ? "pcs-composer-wake" : "", replyLanded ? "pcs-composer-landed" : ""].filter((name) => name.length > 0).join(" ")}>
            <FreeTextInput
              placeholder={composerPlaceholder(onboarding)}
              disabled={busy || gated || introTyping || composerShut(onboarding) !== null}
              disabledReason={gated ? HOME.gated : introTyping ? HOME.introWait : busy ? (onboarding !== null ? (onboarding.stage === "rehearsal" ? HOME.rehearsalReading : HOME.readingYou) : HOME.reading) : (composerShut(onboarding) ?? undefined)}
              onSubmit={onboarding === null ? props.onSend : onboarding.stage === "rehearsal" ? props.onRehearse : props.onOnboard}
              onOpenSettings={props.onOpenSettings}
              dictate={props.dictate}
              fieldRef={composerField}
              compact
            />
          </div>
        </section>

        {/* THE DESK: the play surface's desk panel, and the cards the coach
            dealt onto it, stacked a step apart. It does not clip — a card
            arrives from beyond its left edge and may be dragged across the
            rule onto the conversation — the composition's frame is the wall. */}
        <section data-region="desk" aria-label="Your desk" className="pcs-panel pcs-desk">
          {/* THE DESK'S HEADING (2026-10-31, Andre): its right hand holds the desk's
              tools — the arrange controls (staged) and the bin — where "from AJ"
              used to stand. The foot keeps its one line. */}
          <PanelHeading
            label={HOME.deskLabel}
            tools={
              <div data-testid="desk-tools" className="pcs-desk-tools">
                {arrangeable ? (
                  <button type="button" data-testid="desk-tidy" title={arrangeStyle === "rows" ? HOME.tidyRowsHint : HOME.tidyHint} onClick={tidyDesk}>
                    <ToolGlyph of="tidy" />
                    {HOME.tidy}
                  </button>
                ) : null}
                {arrangeable ? (
                  <ArrangeMenu word={HOME.arrangeBy} hint={HOME.arrangeByHint} choices={arrangeChoices} onChoose={(id) => { if (isArrangeBy(id)) arrangeDesk(id); }} />
                ) : null}
                {arrangeable && beforeArrange !== null ? (
                  <button type="button" data-testid="desk-undo-arrange" title={HOME.undoArrangeHint} onClick={undoArrange}>
                    <ToolGlyph of="undo" />
                    {HOME.undoArrange}
                  </button>
                ) : null}
                {/* THE BIN: a small basket at the heading's right; hot while a dragged card is over it. */}
                <div ref={binRef} data-testid="desk-bin" data-hot={binHot ? "true" : undefined} className="pcs-desk-bin" title={HOME.binHint} aria-label={HOME.bin} role="img">
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                    <path d="M2.5 4.5h11 M6 4.5V3h4v1.5 M3.5 4.5l.8 9.5h7.4l.8-9.5 M6.3 7v5 M8 7v5 M9.7 7v5" />
                  </svg>
                </div>
              </div>
            }
          />

          <div ref={deskRef} className="pcs-front-door-desk">
            {cards.map((card, index) => {
              const key = cardKey(card, index);
              if (dismissed.has(key)) return null;
              return (
                <PushedCard
                  key={key}
                  paperKey={key}
                  card={card}
                  entry={card.caseSlug === null ? null : (bySlug.get(card.caseSlug) ?? null)}
                  index={slotOf(key, index)}
                  offset={offsets[key] ?? { x: 0, y: 0 }}
                  emerging={emerging.has(key)}
                  // The last card pressed sits on top of the stack; untouched cards keep their arrival order beneath.
                  zIndex={10 + (raised.includes(key) ? presentKeys.length + raised.indexOf(key) : slotOf(key, index))}
                  regionRef={regionRef}
                  onTouch={() => touchCard(key)}
                  onEmerged={() => onEmerged(key)}
                  onPlace={(next) => { place(key, next); }}
                  onEnter={() => enter(card)}
                  onDetails={card.caseSlug === null ? null : (rect) => { touchCard(key); box.open(card.caseSlug as string, rect); }}
                  binRef={binRef}
                  onOverBin={setBinHot}
                  onBin={() => putAway(key)}
                  link={paperLinkOf(card)}
                  top={key === topKey}
                />
              );
            })}
            {(!papersReady ? [] : [
              ...(focusFace === null ? [] : [{ key: "focus", face: focusFace, doEnter: enterFocus, doDetails: (rect: DOMRect) => { box.openKind("focus", rect); } }]),
              ...(practiceFace === null ? [] : [{ key: "practice", face: practiceFace, doEnter: () => { enterPractice(); }, doDetails: (rect: DOMRect) => { box.openKind("practice", rect); } }]),
              ...exercises.map((exercise) => ({ key: exerciseKey(exercise), face: exerciseFace(exercise), doEnter: (rect: DOMRect) => { enterExercise(exercise, rect); }, doDetails: (rect: DOMRect) => { box.openKind("practice", rect, exercise.exerciseId); } })),
              // The email card has one door, "open email choices.", and no details: it opens into the choices.
              ...(emailFace === null ? [] : [{ key: "email", face: emailFace, doEnter: (rect: DOMRect) => { touchCard("email"); box.openKind("email", rect); }, doDetails: null }]),
            ] as readonly { key: string; face: PaperFace; doEnter: (rect: DOMRect) => void; doDetails: ((rect: DOMRect) => void) | null }[]).map(({ key, face, doEnter, doDetails }, at) => (
                <DeskPaper
                  key={key}
                  paperKey={key}
                  face={face}
                  index={slotOf(key, cards.length + at)}
                  offset={offsets[key] ?? { x: 0, y: 0 }}
                  emerging={emerging.has(key)}
                  zIndex={10 + (raised.includes(key) ? presentKeys.length + raised.indexOf(key) : slotOf(key, cards.length + at))}
                  regionRef={regionRef}
                  onTouch={() => touchCard(key)}
                  onEmerged={() => onEmerged(key)}
                  onPlace={(next) => { place(key, next); }}
                  onEnter={(rect) => doEnter(rect)}
                  onDetails={doDetails === null ? null : (rect) => { touchCard(key); doDetails(rect); }}
                  binRef={binRef}
                  onOverBin={setBinHot}
                  // The generic practice paper and the email card are the desk's own and cannot be binned.
                  onBin={key === "practice" || key === "email" ? null : () => putAway(key)}
                  top={key === topKey}
                />
            ))}

            {hint !== "hidden" ? (
              <p
                aria-hidden="true"
                data-testid="drag-hint"
                className={`pointer-events-none absolute m-0 font-display text-small font-bold text-ink ${hint === "in" ? "pc-hint-in" : "pc-hint-out"}`}
                style={{ top: "0.75rem", left: `${String(STACK_INSET_REM)}rem`, zIndex: 40 }}
              >
                {HOME.dragHint} <span aria-hidden="true">↘</span>
              </p>
            ) : null}
          </div>

          <div className="pcs-desk-foot">
            <span className="pcs-quiet pcs-truncate">{cards.length === 0 ? (intaking ? "" : HOME.deskEmpty) : HOME.deskFoot}</span>
          </div>
        </section>

      {/* The case's own lightbox, exactly the library's: one at a time, minimised
          by a press on any card or on the coach's panel. */}
      {box.leaving !== null && box.leaving.kind !== "case" ? paperLightbox(box.leaving, true) : null}
      {box.lightbox !== null && box.lightbox.kind !== "case" ? paperLightbox(box.lightbox, false) : null}
      {box.leaving !== null && box.leaving.kind === "case" && boxEntryFor(box.leaving.slug) !== null ? (
        <CaseLightbox
          key={`leave-${String(box.leaving.nonce)}`}
          entry={boxEntryFor(box.leaving.slug) as LibraryCase}
          state={props.libraryState}
          pos={box.leaving.pos}
          leaving
          busy={busy}
          saved={false}
          onMove={() => undefined}
          onClose={() => undefined}
          onContinue={() => undefined}
          onStart={() => undefined}
          onStartOver={() => undefined}
          onReadPrologue={() => undefined}
          onToggleSaved={() => undefined}
        />
      ) : null}
      {box.lightbox !== null && box.lightbox.kind === "case" && boxEntry !== null ? (
        <CaseLightbox
          key={`open-${String(box.lightbox.nonce)}`}
          entry={boxEntry}
          state={props.libraryState}
          pos={box.lightbox.pos}
          leaving={false}
          busy={busy}
          saved={savedSlugs.includes(boxEntry.slug)}
          onMove={box.move}
          onClose={box.close}
          onContinue={props.onContinue}
          onStart={() => {
            props.onStart(boxEntry.slug, false);
          }}
          onStartOver={() => {
            box.setConfirmingStartOver(true);
          }}
          onReadPrologue={() => {
            props.onReadPrologue(boxEntry.slug);
          }}
          onToggleSaved={() => {
            props.onSetSaved(savedSlugs.includes(boxEntry.slug) ? savedSlugs.filter((s) => s !== boxEntry.slug) : [...savedSlugs, boxEntry.slug]);
          }}
        />
      ) : null}
      {box.confirmingStartOver && boxEntry !== null ? (
        <StartOverConfirm
          entry={boxEntry}
          busy={busy}
          onConfirm={() => {
            box.setConfirmingStartOver(false);
            props.onStart(boxEntry.slug, true);
          }}
          onCancel={() => {
            box.setConfirmingStartOver(false);
          }}
        />
      ) : null}
      </div>
    </div>
  );
}

/** What the composer asks for at each stage of onboarding, and the one moment it is shut. The coach's question itself is in the conversation. */
function composerPlaceholder(onboarding: OnboardingState | null): string {
  if (onboarding === null) return HOME.inputPlaceholder;
  if (onboarding.stage === "rehearsal") return HOME.rehearsalPlaceholder;
  if (onboarding.draft !== null) return HOME.correctPlaceholder;
  return HOME.introPlaceholder;
}

function composerShut(onboarding: OnboardingState | null): string | null {
  if (onboarding === null) return null;
  // Past the extractor's rounds, a level can only be picked: the loop ends here by construction.
  if (onboarding.stage === "intake" && onboarding.question?.field === "experience_level" && onboarding.question.plain) return HOME.pickOne;
  // The rehearsal is one move: after the reflection, the only door is "begin."
  if (onboarding.stage === "rehearsal" && onboarding.rehearsal !== null) return HOME.rehearsalDone;
  return null;
}

const EMPTY_PAGE: Page = { key: "", items: [] };

/**
 * The conversation: the coach's opening in its own sans face, then every
 * kept line — the learner's right-aligned in the soft ink with a right rule,
 * the coach's as prose — in the panel's own type. Paged exactly as the story
 * is (storyteller/pages.tsx): a new page each day the learner comes back, or
 * once a page holds a chapter's worth, and each page scrolls.
 */
function CoachThread({
  opening,
  openingFailed,
  firstVisit,
  gated,
  onboarding,
  thread,
  pendingText,
  busy,
  error,
  finishRef,
  onAnswerLevel,
  onConfirmProfile,
  onRedoProfile,
  onSkipRehearsal,
  onBeginAfterRehearsal,
  onViewMoment,
  onTyping,
  links,
  selectedLink,
  onSelectLink,
  reveal,
}: {
  opening: CoachOpening | null;
  openingFailed: boolean;
  firstVisit: boolean;
  gated: boolean;
  onboarding: OnboardingState | null;
  thread: readonly CoachThreadMessage[];
  pendingText: string | null;
  busy: boolean;
  error: string | null;
  /** Filled with "finish the typing at once", for the panel's own press handler. */
  finishRef: { current: () => void };
  /** THE CONNECTIONS (2026-10-31, desk-links.ts): the files the coach's replies put on the desk, which one is open, and the press on a marker. */
  links: ReadonlyMap<string, LinkedFile>;
  selectedLink: string | null;
  onSelectLink: (messageId: string) => void;
  /** The paragraph the desk asked to see ("why this case?"): the book turns to its page and scrolls to it. */
  reveal: { readonly key: string; readonly tick: number } | null;
  onAnswerLevel: (level: ExperienceLevel) => void;
  onConfirmProfile: () => void;
  onRedoProfile: () => void;
  onSkipRehearsal: () => void;
  onBeginAfterRehearsal: () => void;
  onViewMoment?: ((reference: EvidenceReference) => void) | undefined;
  /** Whether the coach's newest words are still being typed, for the composer above. */
  onTyping: (typing: boolean) => void;
}): ReactNode {
  /*
   * THE NEWEST THING THE COACH SAID, revealed as it is written.
   *
   * The conversation the server hands back on ARRIVAL is history: it was said
   * before this page was looking, and typing its last line out again on every
   * visit is the coach repeating yesterday's answer. So the first settled
   * thread is marked as already typed — unless this learner has already said
   * something here, in which case what comes back is the reply to it and
   * types like any other.
   */
  const interacted = useRef(false);
  if (pendingText !== null || busy) interacted.current = true;
  const history = useRef(false);
  if (!history.current && thread.length > 0) {
    history.current = true;
    if (!interacted.current) markTyped(thread.map((line) => line.id));
  }

  const utterance = coachUtterance({ opening, thread, onboarding });
  const typing = useTypewriter(utterance?.key ?? null, utterance?.text ?? "", { typeOnMount: utterance?.key === INTRO_KEY });
  finishRef.current = typing.finish;
  useEffect(() => {
    onTyping(typing.typing);
  }, [typing.typing, onTyping]);

  /*
   * IS THE COACH WRITING? Its portrait flips through its expressions while it
   * is — a reply being composed, the opening being assembled, an onboarding
   * answer being read, the words still landing on the page — and settles the
   * moment the last character is there.
   */
  const writing = !gated && (busy || typing.typing || (onboarding === null && opening === null && !openingFailed));

  const items = useMemo(
    () =>
      coachAtoms({
        opening,
        openingFailed,
        firstVisit,
        gated,
        onboarding,
        thread,
        pendingText,
        busy,
        error,
        typingKey: utterance?.key ?? null,
        typed: typing.shown,
        onAnswerLevel,
        onConfirmProfile,
        onRedoProfile,
        onSkipRehearsal,
        onBeginAfterRehearsal,
        onViewMoment,
        links,
        selectedLink,
        onSelectLink,
      }),
    [opening, openingFailed, firstVisit, gated, onboarding, thread, pendingText, busy, error, utterance?.key, typing.shown, onAnswerLevel, onConfirmProfile, onRedoProfile, onSkipRehearsal, onBeginAfterRehearsal, onViewMoment, links, selectedLink, onSelectLink],
  );

  /*
   * WHERE THE READER LANDS: on the newest page, at the start of the newest
   * exchange — the welcome from its first word when nothing has been said
   * yet (live on 2026-09-10 the first paragraph of every welcome was scrolled
   * off by a reader that followed the typing), or the last thing they said
   * with the answer under it. From there the reader scrolls; saying something
   * turns the book to where the answer lands (storyteller/pages.tsx).
   */
  const pages = useMemo(() => splitPages(items), [items]);
  const newest = useMemo(() => newestOf(items), [items]);
  const place = useReadingPlace(pages.length, "latest");
  const toLatest = place.toLatest;
  useEffect(() => {
    if (pendingText !== null) toLatest();
  }, [pendingText, toLatest]);

  // "WHY THIS CASE?" (2026-10-31): the desk asked for a paragraph. The book
  // turns to the page that holds it; the page then scrolls to it (ScrollPage
  // `showKey`). A paragraph no page holds, a reply since erased, turns nothing.
  const setPage = place.setPage;
  const revealTick = reveal?.tick ?? null;
  const revealKey = reveal?.key ?? null;
  useEffect(() => {
    if (revealTick === null || revealKey === null) return;
    const index = pages.findIndex((page) => page.items.some((item) => item.key === revealKey));
    if (index >= 0) setPage(index);
  }, [revealTick, revealKey, pages, setPage]);

  return (
    <>
      <ScrollPage
        page={pages[place.page] ?? EMPTY_PAGE}
        className="pcs-page pcs-coach-page"
        label={HOME.coachPanel}
        // A polite live region reading out every third character would be
        // unusable; what lands is announced once it has landed.
        live={!typing.typing}
        lead={<CoachPortrait writing={writing} />}
        newest={newest}
        following={place.following}
        latestTick={place.latestTick}
        showKey={reveal}
      />
      {/* THE PAGE CONTROL, just above the composer: the play surface's own,
          count and all ("3 / 7"), with the way back to the newest page in
          the end slot where the story keeps "Latest". Andre, 2026-09-21: the
          page count is navigation, not a reading of the learner, and the
          front door shows it like the play screen does. */}
      <Pager
        page={place.page}
        count={place.count}
        onPage={place.setPage}
        what="conversation"
        end={
          <button type="button" onClick={place.toLatest} disabled={place.page >= place.count - 1}>
            {HOME.pagerLatest}
          </button>
        }
      />
    </>
  );
}

/**
 * One card the coach pushed. Stands behind the panel with its top showing;
 * emerges from fully behind it on arrival; drags anywhere inside the page
 * (walled by the same arithmetic the desk's documents use) and stays where
 * it is put. A press that never moved brings it to the front — never into
 * the case. Its two doors are real buttons at its foot.
 */
/** What a desk paper shows, whatever it is: the case card's face, described in words and one painting. */
export interface PaperFace {
  readonly kindWords: string;
  readonly glyph: CardGlyph;
  /** A bundled symbol id, or null for the plan's ruled strip. */
  readonly coverId: string | null;
  readonly title: string;
  readonly rows: readonly (readonly [string, string])[];
  readonly byline: string;
  /** The first door's word. */
  readonly action: string;
  readonly dataCard: string;
  readonly dataCase?: string | undefined;
  /** The marker under the reason line, on a card a reply of the coach's put here (desk-links.ts). */
  readonly link?: PaperLink | undefined;
}

/** What a paper's marker needs: its end's name for the overlay, whether it is open, the press, and whether the push was for the chosen focus. */
interface PaperLink {
  readonly end: string;
  readonly selected: boolean;
  readonly tone: "plain" | "focus";
  readonly onSelect: () => void;
}

/** `prefers-reduced-motion`: the connection's scroll and its drawing keep still. */
function stillness(): boolean {
  return globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
}

function PushedCard({
  paperKey,
  card,
  entry,
  index,
  offset,
  emerging,
  zIndex,
  regionRef,
  onTouch,
  onEmerged,
  onPlace,
  onEnter,
  onDetails,
  binRef,
  onOverBin,
  onBin = null,
  link,
  top = false,
}: {
  paperKey: string;
  card: CoachCard;
  entry: LibraryCase | null;
  /** THE MARKER on a pushed card (2026-10-31, desk-links.ts): "why this case?", which opens the connection to the reply that made it. */
  link?: PaperLink | undefined;
  /** The paper on top of the stack: the last one the hand raised (glow 1, 2026-11-02). */
  top?: boolean | undefined;
  index: number;
  offset: Point;
  emerging: boolean;
  zIndex: number;
  regionRef: { readonly current: HTMLDivElement | null };
  onTouch: () => void;
  onEmerged: () => void;
  onPlace: (offset: Point) => void;
  onEnter: () => void;
  /** Null on a card that is not a case (the plan). */
  onDetails: ((rect: DOMRect) => void) | null;
  binRef?: { readonly current: HTMLElement | null } | undefined;
  onOverBin?: ((over: boolean) => void) | undefined;
  onBin?: (() => void) | null | undefined;
}): ReactNode {
  // The kind row: the door word, then the track — "continue · conflict
  // management". The plan has no track and stands alone (Codex, 2026-09-22).
  const face: PaperFace = {
    kindWords: entry === null ? HOME.cardKind[card.kind] : `${HOME.cardKind[card.kind]} · ${trackLabel(entry.track)}`,
    glyph: card.kind === "PLAN" ? "plan" : "case",
    coverId: card.caseSlug,
    title: card.title,
    rows: [],
    byline: card.reason,
    action: HOME.cardAction[card.kind],
    dataCard: card.kind,
    dataCase: card.caseSlug ?? undefined,
    ...(link === undefined ? {} : { link }),
  };
  return (
    <DeskPaper
      paperKey={paperKey}
      face={face}
      caseEntry={entry}
      index={index}
      offset={offset}
      emerging={emerging}
      zIndex={zIndex}
      regionRef={regionRef}
      onTouch={onTouch}
      onEmerged={onEmerged}
      onPlace={onPlace}
      onEnter={onEnter}
      onDetails={onDetails}
      binRef={binRef}
      onOverBin={onOverBin}
      onBin={onBin}
      top={top}
    />
  );
}

/**
 * ANY PAPER ON THE DESK (2026-09-23): the case card's body, drawn from a
 * face. A case card passes its library entry for the three rows; the focus
 * and practice papers pass their own rows in words.
 */
function DeskPaper({
  paperKey,
  face,
  caseEntry = null,
  binRef,
  onOverBin,
  onBin = null,
  index,
  offset,
  emerging,
  zIndex,
  regionRef,
  onTouch,
  onEmerged,
  onPlace,
  onEnter,
  onDetails,
  top = false,
}: {
  /** The desk's own name for this paper, on the element as `data-key`, so the desk can measure the paper where it stands. */
  paperKey: string;
  face: PaperFace;
  caseEntry?: LibraryCase | null;
  index: number;
  offset: Point;
  emerging: boolean;
  zIndex: number;
  regionRef: { readonly current: HTMLDivElement | null };
  onTouch: () => void;
  onEmerged: () => void;
  onPlace: (offset: Point) => void;
  onEnter: (rect: DOMRect) => void;
  /** Null on a card that is not a case (the plan). */
  onDetails: ((rect: DOMRect) => void) | null;
  /** The paper on top of the stack, the last one the hand raised: it keeps a quiet edge in the study (glow 1, 2026-11-02). */
  top?: boolean | undefined;
  /** The bin at the desk's foot; a drag that ends over it puts the paper away (null: this paper cannot be binned). */
  binRef?: { readonly current: HTMLElement | null } | undefined;
  onOverBin?: ((over: boolean) => void) | undefined;
  onBin?: (() => void) | null | undefined;
}): ReactNode {
  const ref = useRef<HTMLDivElement | null>(null);
  const [delta, setDelta] = useState<Point | null>(null);
  const origin = useRef<{ card: DOMRect; region: DOMRect } | null>(null);
  /** Alternates on every press so the edge sweep (glow 1) restarts: the stylesheet keys two equal runs on a and b. */
  const [sweep, setSweep] = useState<"a" | "b" | null>(null);

  const walled = (dx: number, dy: number): Point => {
    const o = origin.current;
    if (o === null) return { x: dx, y: dy };
    return clampDragDelta(o.card, o.region, { width: globalThis.innerWidth, height: globalThis.innerHeight }, dx, dy);
  };

  /** Whether the card, moved by (dx, dy) from where the hand took it, lies over the bin. */
  const overBin = (dx: number, dy: number): boolean => {
    const o = origin.current;
    const bin = binRef?.current ?? null;
    if (o === null || bin === null || onBin === null || onBin === undefined) return false;
    const b = bin.getBoundingClientRect();
    const left = o.card.left + dx, top = o.card.top + dy, right = left + o.card.width, bottom = top + o.card.height;
    return left < b.right && right > b.left && top < b.bottom && bottom > b.top;
  };
  const startDrag = usePointerDrag({
    onStart: () => {
      onTouch();
      setSweep((prev) => (prev === "a" ? "b" : "a"));
      // A hand on the card ends its arrival: from here the card is wherever the hand puts it.
      if (emerging) onEmerged();
      const el = ref.current;
      const region = regionRef.current;
      origin.current = el !== null && region !== null ? { card: el.getBoundingClientRect(), region: region.getBoundingClientRect() } : null;
    },
    onMove: (dx, dy) => {
      const d = walled(dx, dy);
      setDelta(d);
      onOverBin?.(overBin(d.x, d.y));
    },
    onEnd: (end) => {
      setDelta(null);
      onOverBin?.(false);
      if (!end.moved) return;
      const d = walled(end.dx, end.dy);
      if (overBin(d.x, d.y) && onBin !== null && onBin !== undefined) { onBin(); return; }
      onPlace({ x: offset.x + d.x, y: offset.y + d.y });
    },
  });

  const x = offset.x + (delta?.x ?? 0);
  const y = offset.y + (delta?.y ?? 0);

  // Stacked a step apart down the desk with a small alternating lean of
  // POSITION, never of angle: a case paper sits straight (Andre, 2026-09-22).
  // The arrival keyframes (app.css .pc-paper-land) slide in from beyond the
  // desk's left edge and end on exactly this resting transform.
  // The resting transform is always set; while the card is arriving the keyframes own the transform
  // and end on the same translate, so a remembered position is where the slide lands (2026-09-23).
  const style: CSSProperties & Record<"--pc-land-dx" | "--pc-rest-x" | "--pc-rest-y" | "--pc-land-delay", string> = {
    left: `${String(STACK_INSET_REM + (index % 2) * STACK_LEAN_REM)}rem`,
    top: `${String(index * STACK_STEP_REM)}rem`,
    width: `${String(CARD_W_REM)}rem`,
    zIndex: delta === null ? zIndex : 90,
    transform: `translate(${String(x)}px, ${String(y)}px)`,
    animationDelay: emerging ? `${String(index * EMERGE_STAGGER_MS)}ms` : undefined,
    "--pc-land-dx": `${String(-(CARD_W_REM + STACK_INSET_REM + 4))}rem`,
    "--pc-rest-x": `${String(x)}px`,
    "--pc-rest-y": `${String(y)}px`,
    // The arrival's stagger, for the light under the heading (glow 3, app.css), which rides the same delay.
    "--pc-land-delay": emerging ? `${String(index * EMERGE_STAGGER_MS)}ms` : "0ms",
  };

  return (
    <div
      ref={ref}
      role="group"
      aria-label={face.title}
      data-key={paperKey}
      data-card={face.dataCard}
      data-case={face.dataCase}
      data-top={top ? "true" : undefined}
      data-sweep={sweep ?? undefined}
      data-dragging={delta === null ? undefined : "true"}
      onPointerDown={startDrag}
      // The arrival ends on the card's own animation, never on a light under its heading (app.css) bubbling up from a child.
      onAnimationEnd={emerging ? (e) => { if (e.target === e.currentTarget) onEmerged(); } : undefined}
      className={[
        "pc-case-paper absolute select-none",
        delta === null ? "cursor-grab transition-transform duration-[var(--pc-dur)] ease-pc" : "cursor-grabbing",
        emerging ? "pc-paper-land" : "",
      ].join(" ")}
      style={style}
    >
      <CaseKindRow words={face.kindWords} glyph={face.glyph} />
      {/* The painting; the plan has none, and the document card's ruled strip stands in. */}
      {face.coverId === null ? <div aria-hidden="true" className="pc-case-rule" /> : face.glyph === "case" ? <CasePaperCover slug={face.coverId} /> : <SymbolCover id={face.coverId} />}
      <h3 className="pc-case-title">{face.title}</h3>
      {caseEntry !== null ? <CardRows entry={caseEntry} /> : face.rows.length > 0 ? <PaperRows rows={face.rows} /> : null}
      {/* Why this card, in the product's one sentence — so a learner told to
          work on one thing is never left guessing why the case in front of
          them trains another. Below the rows, above the doors. */}
      <p className="pc-case-byline">{face.byline}</p>
      {face.link === undefined ? null : (
        <p className="pc-case-link">
          <LinkMark end={face.link.end} word={HOME.linkWhy} hint={HOME.linkWhyHint} selected={face.link.selected} tone={face.link.tone} onSelect={face.link.onSelect} />
        </p>
      )}
      {/* The doors. A press here must not start a drag or count as a press on
          the card, so it stops the pointer before the card sees it. */}
      <div className="pc-case-doors" onPointerDown={(e) => { e.stopPropagation(); }}>
        <button type="button" data-door="enter" onClick={(e) => { onEnter(e.currentTarget.closest("[data-card]")?.getBoundingClientRect() ?? e.currentTarget.getBoundingClientRect()); }}>
          {face.action}
        </button>
        {onDetails !== null ? (
          <button
            type="button"
            data-door="details"
            onClick={(e) => {
              onDetails(e.currentTarget.closest("[data-card]")?.getBoundingClientRect() ?? e.currentTarget.getBoundingClientRect());
            }}
          >
            {HOME.cardDetails}
          </button>
        ) : null}
      </div>
    </div>
  );
}

